import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { StatsCard } from "@/components/progress/StatsCard";
import { BadgeGrid } from "@/components/progress/BadgeGrid";
import { Star, Flame, Trophy, Award } from "lucide-react";
import { getLevelNickname } from "@/utils/levelUtils";
import { useUserProgress } from "@/hooks/useUserProgress";
import { Skeleton } from "@/components/ui/skeleton";

interface UserProgressSectionProps {
  userId: string;
}

export const UserProgressSection = ({ userId }: UserProgressSectionProps) => {
  const { data: progressData, isLoading } = useUserProgress(userId);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          </CardContent>
        </Card>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!progressData) return null;

  const xpToNextLevel = (progressData.level + 1) * 500;
  const currentLevelXP = progressData.level * 500;
  const progressInLevel = ((progressData.xp - currentLevelXP) / (xpToNextLevel - currentLevelXP)) * 100;

  return (
    <div className="space-y-6">
      {/* Progress Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            Progress Stats
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {/* XP & Level */}
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg p-3 text-white text-center">
              <Star className="w-4 h-4 mx-auto mb-1" />
              <div className="text-lg font-bold">Level {progressData.level}</div>
              <div className="text-xs opacity-90">{progressData.xp} XP</div>
              <Progress value={Math.max(0, Math.min(100, progressInLevel))} className="h-1 mt-2 bg-white/20" />
            </div>

            {/* Streak */}
            <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-lg p-3 text-white text-center">
              <Flame className="w-4 h-4 mx-auto mb-1" />
              <div className="text-lg font-bold">{progressData.streak}</div>
              <div className="text-xs opacity-90">day streak</div>
            </div>

            {/* Badge Count */}
            <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-lg p-3 text-white text-center">
              <Award className="w-4 h-4 mx-auto mb-1" />
              <div className="text-lg font-bold">{progressData.badgeCount}</div>
              <div className="text-xs opacity-90">badges earned</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Badges Section */}
      <BadgeGrid badges={progressData.badges} />
    </div>
  );
};